/**
 * AbundanceNightSessionPage - Sleep Immersion Experience
 * 
 * A dedicated sleep screen for the Abundance Night Journey with:
 * - 5-step flow matching the 21 Night Integration
 * - Tapping with chest tap instruction
 * - Recording option (self or AI voice)
 * - Reflection
 * - Sleep playback with ambient music
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BottomNav } from "../../components/BottomNav";
import MicIframeNotice from "../../components/MicIframeNotice";
import ModeSelector from "../../components/audio/ModeSelector";
import AudioEntry from "../../components/audio/AudioEntry";
import useAudioMode from "../../hooks/useAudioMode";
import {
  ChevronLeft, Moon, Play, Pause, Volume2, X, Heart,
  Check, ChevronRight, Sparkles, Mic, Square, Music, Loader2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session steps - matching 21 Night Integration
const STEPS = {
  THEME: 0,
  TAPPING: 1,
  RECORDING: 2,
  REFLECTION: 3,
  PLAYBACK: 4
};

export const AbundanceNightSessionPage = () => {
  const navigate = useNavigate();
  const { nightNumber } = useParams();
  const nightNum = parseInt(nightNumber);

  const [step, setStep] = useState(STEPS.THEME);
  const [nightData, setNightData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tapping state
  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);
  const [tappingComplete, setTappingComplete] = useState(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingType, setRecordingType] = useState("default"); // "default" = Reclaim voice, "self" = Record own
  const [recordingError, setRecordingError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Reflection state
  const [reflectionText, setReflectionText] = useState("");

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [mode, setMode] = useAudioMode();
  const affirmationAudioRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const [modeConfigs, setModeConfigs] = useState({});      // { clear, soft, deep }
  const [modeMusicUrls, setModeMusicUrls] = useState({});
  const [warmingUp, setWarmingUp] = useState(true);

  useEffect(() => {
    fetchNightData();
  }, [nightNum]);

  const fetchNightData = async () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/integration/night/${nightNum}?program=abundance`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to load night");
      }

      const data = await response.json();
      setNightData(data);

      // Mark session as started
      await fetch(`${API_URL}/api/integration/night/${nightNum}/start?program=abundance`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      // Load existing recording if available
      if (data.existing_recording?.audio_data) {
        setRecordingUrl(data.existing_recording.audio_data);
        setRecordingType(data.existing_recording.recording_type || "self");
      }

      // Load existing reflection if available
      if (data.existing_reflection?.reflection_text) {
        setReflectionText(data.existing_reflection.reflection_text);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pre-fetch all 3 unified-audio modes upfront so taps are instant + iOS-safe
  useEffect(() => {
    let cancelled = false;
    const warm = async () => {
      try {
        const ids = ["clear", "soft", "deep"];
        const results = await Promise.all(
          ids.map((m) =>
            fetch(`${API_URL}/api/unified-audio/resolve?area=abundance_night_session&mode=${m}`)
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        );
        if (cancelled) return;
        const cfgs = {};
        const urls = {};
        ids.forEach((m, i) => {
          const data = results[i] || null;
          cfgs[m] = data;
          const u = data?.music?.url;
          urls[m] = u ? (u.startsWith("http") ? u : `${API_URL}${u}`) : null;
        });
        setModeConfigs(cfgs);
        setModeMusicUrls(urls);
      } finally {
        if (!cancelled) setWarmingUp(false);
      }
    };
    warm();
    return () => { cancelled = true; };
  }, []);

  const fetchAmbientStream = async (style) => {
    if (!style) return null;

    try {
      const token = localStorage.getItem("reclaim_token");
      const response = await fetch(`${API_URL}/api/integration/music-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ style })
      });
      
      const data = await response.json();
      if (data.stream_url) {
        const fullUrl = data.stream_url.startsWith('/') ? `${API_URL}${data.stream_url}` : data.stream_url;
        return fullUrl;
      }
    } catch (e) {
      console.error("[Ambient] Error:", e);
    }
    return null;
  };

  const handleNextAffirmation = () => {
    if (currentAffirmationIndex < nightData.affirmations.length - 1) {
      setCurrentAffirmationIndex(prev => prev + 1);
    } else {
      setTappingComplete(true);
      setTimeout(() => setStep(STEPS.RECORDING), 1000);
    }
  };

  // Recording functions
  const startRecording = async () => {
    try {
      setRecordingError(null);
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const errMsg = "Your browser doesn't support audio recording. Please use the AI Voice option.";
        setRecordingError(errMsg);
        toast.error(errMsg);
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else {
          mimeType = '';
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const actualMimeType = mediaRecorderRef.current.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
        setRecordingBlob(blob);
        setRecordingUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      toast.success("Recording started!");
    } catch (err) {
      let errMsg = "Could not access microphone. Please use the AI Voice option.";
      if (err.name === 'NotAllowedError') {
        errMsg = "Microphone access was blocked. Please allow microphone access or use the AI Voice option.";
      }
      setRecordingError(errMsg);
      toast.error(errMsg);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success("Recording stopped!");
    }
  };

  const saveRecording = async () => {
    if (!recordingBlob && !recordingUrl) {
      toast.error("Please record your affirmations first");
      return;
    }

    const token = localStorage.getItem("reclaim_token");
    
    try {
      let audioData = recordingUrl;
      
      if (recordingBlob) {
        const reader = new FileReader();
        audioData = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(recordingBlob);
        });
      }

      await fetch(`${API_URL}/api/integration/recording`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          night: nightNum,
          audio_data: audioData,
          recording_type: recordingType,
          program: "abundance"
        })
      });

      toast.success("Recording saved!");
      setStep(STEPS.REFLECTION);
    } catch (err) {
      toast.error("Failed to save recording");
    }
  };

  const saveReflection = async () => {
    const token = localStorage.getItem("reclaim_token");
    
    try {
      await fetch(`${API_URL}/api/integration/reflection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          night: nightNum,
          reflection_text: reflectionText,
          program: "abundance"
        })
      });

      toast.success("Reflection saved!");
      setStep(STEPS.PLAYBACK);
    } catch (err) {
      toast.error("Failed to save reflection");
    }
  };

  // iOS-CRITICAL: play() must run synchronously inside the user gesture (mode tap)
  const handleModeSelect = (picked) => {
    setMode(picked);
    const cfg = modeConfigs[picked];
    const musicUrl = modeMusicUrls[picked];

    if (affirmationAudioRef.current && recordingUrl) {
      affirmationAudioRef.current.loop = true;
      affirmationAudioRef.current.volume = cfg?.voice?.volume ?? 0.05;
      affirmationAudioRef.current.play().catch(e => {
        console.error("[Playback] Affirmation play error:", e);
      });
    }
    if (ambientAudioRef.current && musicUrl && (cfg?.music_volume ?? 0) > 0) {
      ambientAudioRef.current.src = musicUrl;
      ambientAudioRef.current.loop = true;
      ambientAudioRef.current.volume = cfg?.music_volume ?? 0.65;
      ambientAudioRef.current.play().catch(e => {
        console.error("[Playback] Music play error:", e);
      });
    }
    completeNight();
  };

  const completeNight = async () => {
    const token = localStorage.getItem("reclaim_token");
    
    try {
      const response = await fetch(`${API_URL}/api/integration/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ night: nightNum, program: "abundance" })
      });

      const data = await response.json();
      
      if (data.journey_complete) {
        toast.success("Congratulations! You've completed all 21 nights of the Abundance Journey!");
      } else {
        toast.success(`Night ${nightNum} complete! Rest deeply tonight.`);
      }

      setIsPlaying(true);
    } catch (err) {
      toast.error("Failed to complete night");
    }
  };

  const handleStopPlayback = () => {
    if (affirmationAudioRef.current) affirmationAudioRef.current.pause();
    if (ambientAudioRef.current) ambientAudioRef.current.pause();
    setIsPlaying(false);
  };

  const handleExit = () => {
    if (affirmationAudioRef.current) affirmationAudioRef.current.pause();
    if (ambientAudioRef.current) ambientAudioRef.current.pause();
    navigate("/journey/abundance/nights");
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (affirmationAudioRef.current) affirmationAudioRef.current.pause();
      if (ambientAudioRef.current) ambientAudioRef.current.pause();
    };
  }, []);

  // (volumes are applied per-mode at tap time via modeConfigs)

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] p-6">
        <button onClick={() => navigate("/journey/abundance/nights")} className="flex items-center gap-2 text-indigo-300 mb-6">
          <ChevronLeft size={20} />
          Back
        </button>
        <div className="text-center py-12">
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] pb-20">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button 
          onClick={handleExit}
          className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
        >
          <X size={20} className="text-white" />
        </button>
        <div className="flex items-center gap-2">
          <Moon className="w-5 h-5 text-indigo-400" />
          <span className="text-indigo-200/60 text-sm">Night {nightNum}</span>
        </div>
        <div className="w-10" />
      </div>

      {/* Step Indicators */}
      <div className="px-6 mb-6">
        <div className="flex gap-2">
          {[0, 1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded-full transition-all ${
                s <= step ? "bg-indigo-500" : "bg-white/10"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {step === STEPS.THEME && (
          <ThemeScreen
            key="theme"
            night={nightData}
            onContinue={() => setStep(STEPS.TAPPING)}
          />
        )}
        {step === STEPS.TAPPING && (
          <TappingScreen
            key="tapping"
            affirmations={nightData.affirmations}
            currentIndex={currentAffirmationIndex}
            onNext={handleNextAffirmation}
            isComplete={tappingComplete}
          />
        )}
        {step === STEPS.RECORDING && (
          <RecordingScreen
            key="recording"
            nightNum={nightNum}
            affirmations={nightData.affirmations}
            isRecording={isRecording}
            recordingUrl={recordingUrl}
            recordingType={recordingType}
            recordingError={recordingError}
            onStartRecording={startRecording}
            onStopRecording={stopRecording}
            onSave={saveRecording}
            onTypeChange={setRecordingType}
            onAudioGenerated={(audioUrl) => {
              setRecordingUrl(audioUrl);
              setRecordingBlob(null);
            }}
          />
        )}
        {step === STEPS.REFLECTION && (
          <ReflectionScreen
            key="reflection"
            question={nightData.reflection_question}
            value={reflectionText}
            onChange={setReflectionText}
            onSave={saveReflection}
          />
        )}
        {step === STEPS.PLAYBACK && (
          <PlaybackScreen
            key="playback"
            nightNum={nightNum}
            recordingUrl={recordingUrl}
            mode={mode}
            isPlaying={isPlaying}
            warmingUp={warmingUp}
            onModeSelect={handleModeSelect}
            onStop={handleStopPlayback}
            onExit={handleExit}
            affirmationAudioRef={affirmationAudioRef}
            ambientAudioRef={ambientAudioRef}
          />
        )}
      </AnimatePresence>
      <BottomNav />
    </div>
  );
};

// Screen Components

const ThemeScreen = ({ night, onContinue }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="px-6 py-12 text-center"
  >
    <motion.div
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.2 }}
      className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                flex items-center justify-center mx-auto mb-8 shadow-lg shadow-indigo-500/30"
    >
      <span className="text-4xl font-bold text-white">{night.night}</span>
    </motion.div>

    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.25 }}
      className="text-indigo-400/60 text-xs uppercase tracking-[0.2em] mb-2"
    >
      {night.phase_name}
    </motion.p>

    <motion.h1
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
      className="text-3xl font-heading text-white mb-4"
    >
      {night.theme}
    </motion.h1>

    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4 }}
      className="text-indigo-200/70 mb-12 max-w-sm mx-auto leading-relaxed"
    >
      {night.description}
    </motion.p>

    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      onClick={onContinue}
      className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full
               text-white font-medium shadow-lg shadow-indigo-500/30
               hover:shadow-indigo-500/50 transition-all"
      data-testid="begin-night-btn"
    >
      Begin Night Practice
    </motion.button>
  </motion.div>
);

const TappingScreen = ({ affirmations, currentIndex, onNext, isComplete }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="px-6 py-8 flex flex-col items-center justify-center min-h-[60vh]"
  >
    {!isComplete ? (
      <>
        <div className="text-center mb-8">
          <span className="text-indigo-400 text-sm">
            {currentIndex + 1} of {affirmations.length}
          </span>
        </div>

        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-12"
        >
          <p className="text-2xl text-white font-light leading-relaxed max-w-sm">
            "{affirmations[currentIndex]}"
          </p>
        </motion.div>

        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="mb-8"
        >
          <Heart className="w-8 h-8 text-indigo-400/50" />
        </motion.div>

        <p className="text-indigo-200/50 text-sm mb-6">
          Tap your chest gently while reading
        </p>

        <button
          onClick={onNext}
          className="px-6 py-3 bg-indigo-500/20 hover:bg-indigo-500/30 
                   rounded-full text-indigo-300 transition-all flex items-center gap-2"
          data-testid="next-affirmation-btn"
        >
          Next Affirmation
          <ChevronRight size={18} />
        </button>
      </>
    ) : (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
      >
        <Sparkles className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
        <p className="text-xl text-white">Beautifully done</p>
        <p className="text-indigo-200/60 mt-2">Moving to recording...</p>
      </motion.div>
    )}
  </motion.div>
);

// Default Reclaim Voice Section (Michael's voice)
const DefaultVoiceSection = ({ nightNum, program = "abundance", onAudioGenerated }) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [notYetRecorded, setNotYetRecorded] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setNotYetRecorded(false);
    
    const token = localStorage.getItem("reclaim_token");
    
    try {
      // Use default Reclaim voice (Michael's voice)
      const response = await fetch(`${API_URL}/api/integration/generate-default-voice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ night: nightNum, program })
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if it's because audio isn't recorded yet
        if (data.detail && data.detail.includes("not yet recorded")) {
          setNotYetRecorded(true);
          return;
        }
        throw new Error(data.detail || "Failed to load voice");
      }

      if (data.audio_url) {
        // Handle both relative and absolute URLs
        const fullUrl = data.audio_url.startsWith('/') 
          ? `${API_URL}${data.audio_url}` 
          : data.audio_url;
        onAudioGenerated(fullUrl);
        toast.success("Reclaim voice ready!");
      } else {
        throw new Error("No audio received");
      }
    } catch (err) {
      let errorMsg = err.message || "Failed to load Reclaim voice";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  // Show message if abundance recordings not yet available
  if (notYetRecorded) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
          <Mic className="w-8 h-8 text-amber-400" />
        </div>
        <p className="text-white font-medium mb-2">Coming Soon</p>
        <p className="text-indigo-200/60 text-sm max-w-sm mx-auto mb-4">
          Michael's Reclaim Voice for the Abundance Journey is being recorded. 
          For now, please use <strong>"Record Own Voice"</strong> — your own voice 
          is the most powerful tool for subconscious transformation.
        </p>
        <p className="text-indigo-400/60 text-xs">
          Switch to "Record Own Voice" above ↑
        </p>
      </div>
    );
  }

  return (
    <div className="text-center py-8">
      <p className="text-indigo-200/60 mb-4 max-w-sm mx-auto">
        Use Michael's voice to guide your subconscious integration tonight.
      </p>
      
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}
      
      <button
        onClick={handleGenerate}
        disabled={generating}
        className={`px-6 py-3 rounded-full transition-all flex items-center gap-2 mx-auto
          ${generating 
            ? "bg-indigo-500/30 text-indigo-300 cursor-wait" 
            : "bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30"
          }`}
      >
        {generating ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading...
          </>
        ) : (
          <>
            <Volume2 className="w-5 h-5" />
            Use Reclaim Voice
          </>
        )}
      </button>
    </div>
  );
};

const RecordingScreen = ({ 
  nightNum,
  affirmations = [],
  isRecording, 
  recordingUrl, 
  recordingType,
  recordingError,
  onStartRecording, 
  onStopRecording, 
  onSave,
  onTypeChange,
  onAudioGenerated
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentReadingIndex, setCurrentReadingIndex] = useState(0);
  const audioRef = useRef(null);

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Auto-advance highlighted affirmation while recording
  useEffect(() => {
    let interval;
    if (isRecording && affirmations.length > 0) {
      interval = setInterval(() => {
        setCurrentReadingIndex(prev => 
          prev < affirmations.length - 1 ? prev + 1 : prev
        );
      }, 4000); // 4 seconds per affirmation
    }
    return () => clearInterval(interval);
  }, [isRecording, affirmations.length]);

  // Reset reading index when starting new recording
  useEffect(() => {
    if (isRecording) {
      setCurrentReadingIndex(0);
    }
  }, [isRecording]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="px-6 py-8"
    >
      <div className="text-center mb-6">
        <Mic className="w-10 h-10 text-indigo-400 mx-auto mb-3" />
        <h2 className="text-xl font-heading text-white mb-2">Record Your Affirmations</h2>
        <p className="text-indigo-200/60 text-sm max-w-sm mx-auto">
          Your voice has powerful influence over your subconscious mind.
        </p>
      </div>

      {/* Recording Type Toggle */}
      <div className="flex gap-2 justify-center mb-6">
        <button
          onClick={() => onTypeChange("default")}
          className={`px-4 py-2 rounded-full text-sm transition-all ${
            recordingType === "default"
              ? "bg-indigo-500 text-white"
              : "bg-white/5 text-indigo-200/60"
          }`}
        >
          Reclaim Voice
        </button>
        <button
          onClick={() => onTypeChange("self")}
          className={`px-4 py-2 rounded-full text-sm transition-all ${
            recordingType === "self"
              ? "bg-indigo-500 text-white"
              : "bg-white/5 text-indigo-200/60"
          }`}
        >
          Record Own Voice
        </button>
      </div>

      {recordingType === "self" ? (
        <div className="flex flex-col items-center">
          <div className="w-full max-w-sm mb-4">
            <MicIframeNotice accent="#A5B4FC" />
          </div>
          {/* Affirmations List - Read While Recording */}
          <div className="w-full max-w-sm mb-6 bg-white/5 rounded-xl p-4 max-h-48 overflow-y-auto">
            <p className="text-indigo-400/60 text-xs uppercase tracking-wider mb-3 text-center">
              Read these affirmations aloud
            </p>
            <div className="space-y-2">
              {affirmations.map((affirmation, index) => (
                <motion.div
                  key={index}
                  animate={{
                    backgroundColor: isRecording && index === currentReadingIndex 
                      ? "rgba(99, 102, 241, 0.2)" 
                      : "transparent",
                    scale: isRecording && index === currentReadingIndex ? 1.02 : 1
                  }}
                  className={`p-2 rounded-lg transition-all ${
                    isRecording && index === currentReadingIndex
                      ? "border border-indigo-500/50"
                      : index < currentReadingIndex && isRecording
                        ? "opacity-40"
                        : ""
                  }`}
                >
                  <p className={`text-sm ${
                    isRecording && index === currentReadingIndex
                      ? "text-white font-medium"
                      : "text-indigo-200/70"
                  }`}>
                    {index + 1}. "{affirmation}"
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Record Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={isRecording ? onStopRecording : onStartRecording}
            className={`w-20 h-20 rounded-full flex items-center justify-center mb-4
                      ${isRecording 
                        ? "bg-red-500 animate-pulse" 
                        : "bg-gradient-to-br from-indigo-500 to-purple-600"
                      } shadow-lg`}
          >
            {isRecording ? (
              <Square className="w-7 h-7 text-white" />
            ) : (
              <Mic className="w-9 h-9 text-white" />
            )}
          </motion.button>

          {isRecording ? (
            <p className="text-red-400 text-sm mb-4">Recording... Tap to stop</p>
          ) : (
            <p className="text-indigo-200/50 text-xs mb-4">Tap to start recording</p>
          )}

          {recordingError && !isRecording && (
            <div className="w-full max-w-sm mb-4 p-3 bg-red-500/20 rounded-lg text-red-300 text-sm text-center">
              {recordingError}
            </div>
          )}

          {recordingUrl && !isRecording && (
            <div className="w-full max-w-sm bg-white/5 rounded-xl p-4 mb-4">
              <audio 
                ref={audioRef} 
                src={recordingUrl}
                onEnded={() => setIsPlaying(false)}
              />
              <div className="flex items-center gap-4">
                <button
                  onClick={togglePlayback}
                  className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-indigo-400" />
                  ) : (
                    <Play className="w-5 h-5 text-indigo-400 ml-0.5" />
                  )}
                </button>
                <div className="flex-1">
                  <p className="text-white text-sm">Your Recording</p>
                  <p className="text-indigo-200/50 text-xs">Tap to preview</p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <DefaultVoiceSection 
          nightNum={nightNum}
          program="abundance"
          onAudioGenerated={onAudioGenerated}
        />
      )}

      {recordingUrl && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8"
        >
          <button
            onClick={onSave}
            className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-600 
                     rounded-full text-white font-medium"
            data-testid="save-recording-btn"
          >
            Save & Continue
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

const ReflectionScreen = ({ question, value, onChange, onSave }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="px-6 py-8"
  >
    <div className="text-center mb-8">
      <Sparkles className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
      <h2 className="text-xl font-heading text-white mb-2">Gentle Reflection</h2>
      <p className="text-indigo-200/60 text-sm">
        Before sleep, reflect on this question
      </p>
    </div>

    <div className="bg-white/5 rounded-2xl p-6 mb-6">
      <p className="text-lg text-white italic text-center">
        "{question}"
      </p>
    </div>

    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Write your reflection..."
      className="w-full h-32 bg-white/5 border border-indigo-500/20 rounded-xl p-4
               text-white placeholder-indigo-200/30 resize-none focus:outline-none
               focus:border-indigo-500/50 transition-colors"
      data-testid="reflection-input"
    />

    <button
      onClick={onSave}
      className="w-full py-4 mt-6 bg-gradient-to-r from-indigo-500 to-purple-600 
               rounded-full text-white font-medium"
      data-testid="save-reflection-btn"
    >
      Save Reflection
    </button>

    <button
      onClick={onSave}
      className="w-full py-3 mt-3 text-indigo-300/60 text-sm"
    >
      Skip for tonight
    </button>
  </motion.div>
);

const PlaybackScreen = ({
  nightNum,
  recordingUrl,
  mode,
  isPlaying,
  warmingUp,
  onModeSelect,
  onStop,
  onExit,
  affirmationAudioRef,
  ambientAudioRef
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="px-6 py-10"
    >
      {/* Hidden Audio Elements */}
      <audio
        ref={affirmationAudioRef}
        src={recordingUrl}
        preload="auto"
        playsInline
      />
      <audio ref={ambientAudioRef} preload="auto" playsInline />

      {!isPlaying ? (
        <AudioEntry
          title={`Night ${nightNum}`}
          subtitle="Sleep Integration"
          defaultMode="deep"
          selected={mode}
          onSelect={onModeSelect}
          busy={warmingUp}
          area="abundance_night_session"
        />
      ) : (
        <div className="text-center max-w-md mx-auto">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/20 rounded-full mb-4">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-400">Playing — Night {nightNum} Complete</span>
          </div>
          <p className="text-indigo-200/50 text-sm mb-4">
            Playback will continue while you sleep
          </p>
          <button
            onClick={onStop}
            className="px-6 py-2 bg-white/10 rounded-full text-white/70 text-sm mr-4"
          >
            Stop Playback
          </button>
          <button
            onClick={onExit}
            className="text-indigo-300/60 text-sm"
          >
            Close & Continue Playing
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default AbundanceNightSessionPage;
